import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import Avatar, { STATUS_LABELS } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth.jsx";
import { useNavigate } from "react-router-dom";
import { MessageSquare, Edit3, UserCircle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Profile card popover — opens when a user's avatar is clicked.
 *
 * Shows: avatar, name, title, department, effective presence, short status, current room.
 * Actions: Send DM (any user). Edit profile / Change avatar (self only).
 */
export default function ProfileCard({ user, presence, room, children, onEditProfile }) {
  const { user: me } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const isMe = me?.id === user?.id;
  const status = presence?.status || user?.status || "available";
  const online = presence?.online != null ? presence.online : (status !== "offline");
  const effectiveStatus = online ? status : "offline";
  const shortStatus = user?.short_status || presence?.short_status;

  const sendDM = async () => {
    if (isMe) return;
    try {
      const { data } = await api.post("/conversations", { user_ids: [user.id] });
      setOpen(false);
      nav(`/app/dm/${data.id}`);
    } catch {
      toast.error("Failed to open DM");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-72 p-4" data-testid={`profile-card-${user?.id}`}>
        <div className="flex flex-col items-center text-center">
          <Avatar user={user} size="xxl" status={effectiveStatus} online={online} showStatus/>
          <div className="mt-3 font-semibold text-base" data-testid="profile-card-name">{user?.name}</div>
          <div className="text-xs text-zinc-500 flex flex-wrap justify-center gap-x-2">
            {user?.title && <span>{user.title}</span>}
            {user?.department && <span className="text-zinc-300">·</span>}
            {user?.department && <span>{user.department}</span>}
          </div>
          <div className="mt-2 text-xs">
            <span className="rounded-full px-2 py-0.5" style={{ background: online ? "#dcfce7" : "#f4f4f5", color: online ? "#166534" : "#71717a" }}>
              {STATUS_LABELS[effectiveStatus] || effectiveStatus}
            </span>
          </div>
          {shortStatus && <div className="mt-2 text-xs text-zinc-600 italic">&ldquo;{shortStatus}&rdquo;</div>}
          {room && <div className="mt-1 text-[11px] text-zinc-500">In: <span className="font-medium">{room}</span></div>}
        </div>
        <div className="mt-4 grid gap-2">
          {isMe ? (
            <>
              <Button variant="outline" size="sm" onClick={() => { setOpen(false); onEditProfile?.(); }} data-testid="profile-card-edit">
                <Edit3 className="w-4 h-4 mr-1"/> Edit profile
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setOpen(false); onEditProfile?.("avatar"); }} data-testid="profile-card-change-avatar">
                <UserCircle className="w-4 h-4 mr-1"/> Change avatar
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={sendDM} data-testid="profile-card-dm">
              <MessageSquare className="w-4 h-4 mr-1"/> Send direct message
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
